import React, { useState, useEffect } from 'react';
import config from '../config';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Typography,
  Tooltip,
  Autocomplete
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Check as CheckIcon, Close as CloseIcon, Warning as WarningIcon, Print as PrintIcon, Gavel as GavelIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useConfirm } from '../hooks/useConfirm';
import { warningsService } from '../services/warnings.service';
import employeesService from '../services/employees.service';
import { branchesService } from '../services/branches.service';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppSelector } from '../hooks/redux.hooks';
import { Warning } from '../types/warnings.types';
import { Employee } from '../types/employees.types';
import { Branch } from '../types/branches.types';
import WarningDialog from '../components/dialogs/WarningDialog';
import AttachmentSection from '../components/common/AttachmentSection';

const WarningsPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const confirm = useConfirm();
  const permissions = useAppSelector((state) => state.auth.permissions);

  const userBranchIds = JSON.parse(localStorage.getItem('userBranchIds') || '[]');
  const canEditWarning = (branchId: number) => {
    return userBranchIds.length === 0 || userBranchIds.includes(branchId);
  };

  const [warnings, setWarnings] = useState<Warning[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState<Warning | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  
  const [selectedBranch, setSelectedBranch] = useState<number | ''>('');
  const [selectedEmployee, setSelectedEmployee] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [activeOnly, setActiveOnly] = useState(false);

  const t = {
    title: isRTL ? 'إدارة الإنذارات' : 'Warnings Management',
    addNew: isRTL ? 'إضافة إنذار' : 'Add Warning',
    branch: isRTL ? 'الفرع' : 'Branch',
    employee: isRTL ? 'الموظف' : 'Employee',
    status: isRTL ? 'الحالة' : 'Status',
    search: isRTL ? 'بحث' : 'Search',
    clear: isRTL ? 'مسح' : 'Clear',
    print: isRTL ? 'طباعة' : 'Print',
    warningType: isRTL ? 'نوع الإنذار' : 'Warning Type',
    violationType: isRTL ? 'نوع المخالفة' : 'Violation Type',
    issueDate: isRTL ? 'تاريخ الإصدار' : 'Issue Date',
    warningNumber: isRTL ? 'رقم الإنذار' : 'Warning No.',
    penalty: isRTL ? 'الجزاء' : 'Penalty',
    approval: isRTL ? 'الاعتماد' : 'Approval',
    actions: isRTL ? 'الإجراءات' : 'Actions',
    active: isRTL ? 'فعال' : 'Active',
    expired: isRTL ? 'منتهي' : 'Expired',
    cancelled: isRTL ? 'ملغي' : 'Cancelled',
    appealed: isRTL ? 'تم الاستئناف' : 'Appealed',
    all: isRTL ? 'الكل' : 'All',
    activeOnly: isRTL ? 'الفعالة فقط' : 'Active Only',
    loadError: isRTL ? 'خطأ في تحميل البيانات' : 'Error loading data',
    deleteSuccess: isRTL ? 'تم الحذف بنجاح' : 'Deleted successfully',
    approveSuccess: isRTL ? 'تمت الموافقة بنجاح' : 'Approved successfully',
    approveError: isRTL ? 'خطأ في الاعتماد' : 'Error approving',
    confirmDelete: isRTL ? 'هل أنت متأكد من الحذف؟' : 'Are you sure?',
    reportTitle: isRTL ? 'تقرير الإنذارات' : 'Warnings Report',
    count: isRTL ? 'العدد' : 'Count',
    totalPenalty: isRTL ? 'إجمالي الجزاءات' : 'Total Penalties',
    activeCount: isRTL ? 'الفعالة' : 'Active',
    noData: isRTL ? 'لا توجد بيانات' : 'No data found',
    managerApproval: isRTL ? 'اعتماد المدير' : 'Manager Approval',
    hrApproval: isRTL ? 'اعتماد HR' : 'HR Approval'
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [warningsData, employeesData, branchesData, companyData] = await Promise.all([
        warningsService.getAll(),
        employeesService.getAll(),
        branchesService.getAll(),
        companyInfoService.getCompanyInfo().catch(() => null)
      ]);
      
      const filteredWarnings = warningsData.filter((warning: Warning) => 
        canEditWarning(warning.branchId)
      );
      
      setWarnings(filteredWarnings);
      setEmployees(employeesData.data || employeesData);
      setBranches(branchesData);
      setCompanyInfo(companyData);
    } catch (error) {
      toast.error(t.loadError);
    }
  };

  const handleSearch = async () => {
    try {
      const data = await warningsService.getAll({
        branchId: selectedBranch || undefined,
        employeeId: selectedEmployee || undefined,
        status: statusFilter || undefined,
        activeOnly
      });
      
      const filteredData = data.filter((warning: Warning) => 
        canEditWarning(warning.branchId)
      );
      
      setWarnings(filteredData);
    } catch (error) {
      toast.error(t.loadError);
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({ message: t.confirmDelete, severity: 'error' }))) return;
    try {
      await warningsService.delete(id);
      toast.success(t.deleteSuccess);
      await loadData();
    } catch (error) {
      toast.error(t.loadError);
    }
  };

  const handleAcknowledge = async (id: number) => {
    try {
      await warningsService.acknowledge(id);
      toast.success(isRTL ? 'تمت الموافقة الأولية' : 'Acknowledged');
      await loadData();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t.approveError);
    }
  };

  const handleApprove = async (id: number, status: 'Approved' | 'Rejected') => {
    try {
      await warningsService.approve({ warningId: id, approverRole: 'HR', notes: status === 'Rejected' ? 'Rejected' : undefined });
      toast.success(t.approveSuccess);
      await loadData();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t.approveError);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'error';
      case 'Acknowledged': return 'warning';
      case 'Approved': return 'success';
      case 'Expired': return 'default';
      case 'Cancelled': return 'warning';
      case 'Appealed': return 'info';
      default: return 'default';
    }
  };

  const getWarningTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      Verbal: isRTL ? 'شفهي' : 'Verbal',
      FirstWritten: isRTL ? 'كتابي أول' : '1st Written',
      SecondWritten: isRTL ? 'كتابي ثاني' : '2nd Written',
      FinalWritten: isRTL ? 'كتابي نهائي' : 'Final Written',
      FinancialPenalty: isRTL ? 'جزاء مالي' : 'Financial Penalty',
      Suspension: isRTL ? 'إيقاف' : 'Suspension',
      Termination: isRTL ? 'فصل' : 'Termination'
    };
    return labels[type] || type;
  };

  const getViolationTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      LateComing: isRTL ? 'تأخير' : 'Late Coming',
      Absence: isRTL ? 'غياب' : 'Absence',
      PerformanceIssue: isRTL ? 'أداء ضعيف' : 'Performance Issue',
      Misconduct: isRTL ? 'سوء سلوك' : 'Misconduct',
      PolicyViolation: isRTL ? 'مخالفة سياسات' : 'Policy Violation',
      SafetyViolation: isRTL ? 'مخالفة أمان' : 'Safety Violation',
      Other: isRTL ? 'أخرى' : 'Other'
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      Active: isRTL ? 'فعال' : 'Active',
      Acknowledged: isRTL ? 'موافقة أولية' : 'Acknowledged',
      Approved: isRTL ? 'معتمد' : 'Approved',
      Expired: isRTL ? 'منتهي' : 'Expired',
      Cancelled: isRTL ? 'ملغي' : 'Cancelled',
      Appealed: isRTL ? 'تم الاستئناف' : 'Appealed'
    };
    return labels[status] || status;
  };

  const getStatusPrintColor = (status: string) => {
    switch (status) {
      case 'Active': return '#f44336';
      case 'Expired': return '#9e9e9e';
      case 'Cancelled': return '#ff9800';
      case 'Appealed': return '#2196f3';
      default: return '#9e9e9e';
    }
  };

  const getWarningTypePrintColor = (type: string) => {
    switch (type) {
      case 'Verbal': return '#4caf50';
      case 'FirstWritten': return '#ff9800';
      case 'SecondWritten': return '#ff5722';
      case 'FinalWritten': return '#f44336';
      case 'FinancialPenalty': return '#e91e63';
      case 'Suspension': return '#9c27b0';
      case 'Termination': return '#b71c1c';
      default: return '#9e9e9e';
    }
  };

  const handlePrint = () => {
    if (warnings.length === 0) {
      toast.info(t.noData);
      return;
    }

    const totalPenalty = warnings.reduce((sum, w) => sum + (w.penaltyAmount || 0), 0);
    const activeCount = warnings.filter(w => w.status === 'Active').length;
    const branchName = selectedBranch ? branches.find(b => b.branchId === selectedBranch) : null;
    const employeeName = selectedEmployee ? employees.find(e => e.employeeId === selectedEmployee) : null;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;

    printWindow.document.write(`
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${t.reportTitle}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; direction: ${isRTL ? 'rtl' : 'ltr'}; color: #333; font-size: 12px; }
          .header { text-align: center; border-bottom: 3px solid #9c27b0; padding-bottom: 15px; margin-bottom: 15px; }
          .header img { max-height: 60px; margin-bottom: 8px; }
          .header h2 { color: #9c27b0; margin: 5px 0; }
          .header .subtitle { font-size: 11px; color: #666; }
          .report-title { text-align: center; margin-bottom: 15px; }
          .report-title h3 { color: #9c27b0; font-size: 20px; }
          .filters-box { border: 1px solid #ddd; border-radius: 6px; padding: 10px 15px; margin-bottom: 15px; background: #f3e5f5; }
          .filters-box span { font-size: 12px; margin-${isRTL ? 'left' : 'right'}: 15px; }
          .filters-box strong { color: #9c27b0; }
          .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 15px; }
          .summary-card { border: 2px solid #9c27b0; border-radius: 8px; padding: 12px; text-align: center; }
          .summary-card .value { font-size: 22px; font-weight: bold; color: #9c27b0; }
          .summary-card .label { font-size: 11px; color: #666; margin-top: 3px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          thead th { background: #9c27b0; color: white; padding: 8px 6px; font-size: 11px; text-align: ${isRTL ? 'right' : 'left'}; }
          thead th.center { text-align: center; }
          thead th.amount { text-align: ${isRTL ? 'left' : 'right'}; }
          tbody td { padding: 6px; border-bottom: 1px solid #eee; font-size: 11px; }
          tbody td.center { text-align: center; }
          tbody td.amount { text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; }
          tbody tr:nth-child(even) { background: #f3e5f5; }
          .status-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: bold; color: white; }
          .type-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: bold; color: white; }
          .approval-badge { display: inline-block; padding: 1px 6px; border-radius: 8px; font-size: 9px; font-weight: bold; color: white; margin: 1px; }
          .total-row td { background: #9c27b0; color: white; font-weight: bold; font-size: 12px; padding: 8px 6px; }
          .employee-name { font-weight: bold; }
          .employee-code { font-size: 10px; color: #888; }
          .signatures { display: flex; justify-content: space-between; margin-top: 50px; }
          .sig-block { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #333; padding-top: 8px; margin-top: 50px; font-weight: bold; font-size: 12px; }
          .print-date { text-align: ${isRTL ? 'left' : 'right'}; font-size: 10px; color: #999; margin-bottom: 10px; }
          @media print { @page { size: A4 landscape; margin: 0.8cm; } }
        </style>
      </head>
      <body>
        <div class="print-date">${isRTL ? 'تاريخ الطباعة:' : 'Print Date:'} ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>

        ${companyInfo ? `
        <div class="header">
          ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
          <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
          <div class="subtitle">
            ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
          </div>
        </div>
        ` : ''}

        <div class="report-title">
          <h3>${t.reportTitle}</h3>
        </div>

        ${(selectedBranch || selectedEmployee || statusFilter) ? `
        <div class="filters-box">
          ${branchName ? `<span><strong>${t.branch}:</strong> ${isRTL ? branchName.branchNameAr : branchName.branchName}</span>` : ''}
          ${employeeName ? `<span><strong>${t.employee}:</strong> ${isRTL ? employeeName.fullNameAr : employeeName.fullName}</span>` : ''}
          ${statusFilter ? `<span><strong>${t.status}:</strong> ${getStatusLabel(statusFilter)}</span>` : ''}
        </div>
        ` : ''}

        <div class="summary-grid">
          <div class="summary-card">
            <div class="value">${warnings.length}</div>
            <div class="label">${t.count}</div>
          </div>
          <div class="summary-card">
            <div class="value">${activeCount}</div>
            <div class="label">${t.activeCount}</div>
          </div>
          <div class="summary-card">
            <div class="value">${totalPenalty.toFixed(3)}</div>
            <div class="label">${t.totalPenalty} (KD)</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>${t.warningNumber}</th>
              <th>${t.employee}</th>
              <th>${t.branch}</th>
              <th>${t.warningType}</th>
              <th>${t.violationType}</th>
              <th>${t.issueDate}</th>
              <th class="amount">${t.penalty}</th>
              <th class="center">${t.approval}</th>
              <th class="center">${t.status}</th>
            </tr>
          </thead>
          <tbody>
            ${warnings.map((w, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td style="font-weight:bold; color:#9c27b0;">${w.warningNumber}</td>
              <td>
                <span class="employee-name">${isRTL ? w.employeeNameAr : w.employeeName}</span>
                <br/><span class="employee-code">${w.employeeCode}</span>
              </td>
              <td>${w.branchName || '-'}</td>
              <td><span class="type-badge" style="background:${getWarningTypePrintColor(w.warningType)}">${getWarningTypeLabel(w.warningType)}</span></td>
              <td>${getViolationTypeLabel(w.violationType)}</td>
              <td>${new Date(w.issueDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</td>
              <td class="amount">${w.penaltyAmount ? w.penaltyAmount.toFixed(3) : '-'}</td>
              <td class="center">
                ${w.managerApprovalDate ? `<span class="approval-badge" style="background:#4caf50">${isRTL ? 'المدير ✓' : 'Mgr ✓'}</span>` : `<span class="approval-badge" style="background:#ff9800">${isRTL ? 'المدير ⏳' : 'Mgr ⏳'}</span>`}
                ${w.hrApprovalDate ? `<span class="approval-badge" style="background:#4caf50">HR ✓</span>` : `<span class="approval-badge" style="background:#ff9800">HR ⏳</span>`}
              </td>
              <td class="center"><span class="status-badge" style="background:${getStatusPrintColor(w.status)}">${getStatusLabel(w.status)}</span></td>
            </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="7" style="text-align: ${isRTL ? 'left' : 'right'};">${isRTL ? 'الإجمالي' : 'Total'}</td>
              <td class="amount">${totalPenalty.toFixed(3)} KD</td>
              <td colspan="2"></td>
            </tr>
          </tbody>
        </table>

        <div class="signatures">
          <div class="sig-block"><div class="sig-line">${isRTL ? 'مدير الموارد البشرية' : 'HR Manager'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المدير المالي' : 'Financial Manager'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المدير العام' : 'General Manager'}</div></div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // printWindow stays open for user review
    }, 500);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t.title}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={isRTL ? 'تحديث' : 'Refresh'}>
            <IconButton onClick={loadData} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} color="secondary">{t.print}</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedWarning(null); setDialogOpen(true); }}>{t.addNew}</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Autocomplete
            options={branches}
            getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
            value={branches.find(b => b.branchId === selectedBranch) || null}
            onChange={(_, newValue) => setSelectedBranch(newValue?.branchId || '')}
            renderInput={(params) => <TextField {...params} label={t.branch} size="small" />}
            size="small"
          />
          <Autocomplete
            options={employees}
            getOptionLabel={(option) => `${option.employeeCode} - ${isRTL ? option.fullNameAr : option.fullName}`}
            value={employees.find(e => e.employeeId === selectedEmployee) || null}
            onChange={(_, newValue) => setSelectedEmployee(newValue?.employeeId || '')}
            renderInput={(params) => <TextField {...params} label={t.employee} size="small" />}
            size="small"
          />
          <TextField select label={t.status} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="small">
            <MenuItem value="">{t.all}</MenuItem>
            <MenuItem value="Active">{t.active}</MenuItem>
            <MenuItem value="Acknowledged">{getStatusLabel('Acknowledged')}</MenuItem>
            <MenuItem value="Approved">{getStatusLabel('Approved')}</MenuItem>
            <MenuItem value="Expired">{t.expired}</MenuItem>
            <MenuItem value="Cancelled">{t.cancelled}</MenuItem>
            <MenuItem value="Appealed">{t.appealed}</MenuItem>
          </TextField>
          <TextField select label={t.activeOnly} value={activeOnly ? '1' : '0'} onChange={(e) => setActiveOnly(e.target.value === '1')} size="small">
            <MenuItem value="0">{t.all}</MenuItem>
            <MenuItem value="1">{t.activeOnly}</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={handleSearch} fullWidth>{t.search}</Button>
            <Button variant="outlined" onClick={() => { setSelectedBranch(''); setSelectedEmployee(''); setStatusFilter(''); setActiveOnly(false); loadData(); }} fullWidth>{t.clear}</Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t.warningNumber}</TableCell>
              <TableCell>{t.employee}</TableCell>
              <TableCell>{t.warningType}</TableCell>
              <TableCell>{t.violationType}</TableCell>
              <TableCell>{t.issueDate}</TableCell>
              <TableCell align="right">{t.penalty}</TableCell>
              <TableCell>{t.status}</TableCell>
              <TableCell align="center">{t.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {warnings.map((warning) => (
              <TableRow key={warning.warningId}>
                <TableCell>{warning.warningNumber}</TableCell>
                <TableCell>{isRTL ? warning.employeeNameAr : warning.employeeName}</TableCell>
                <TableCell>
                  <Chip
                    label={getWarningTypeLabel(warning.warningType)}
                    size="small"
                    sx={{ bgcolor: getWarningTypePrintColor(warning.warningType), color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell>{getViolationTypeLabel(warning.violationType)}</TableCell>
                <TableCell>{new Date(warning.issueDate).toLocaleDateString('en-GB')}</TableCell>
                <TableCell align="right">{warning.penaltyAmount ? `${warning.penaltyAmount.toFixed(3)} KD` : '-'}</TableCell>
                <TableCell>
                  {warning.status === 'Acknowledged' && warning.opsManagerAcknowledged ? (
                    <Tooltip title={`${isRTL ? 'وافق عليها' : 'Acknowledged by'}: ${warning.opsManagerName || ''} - ${warning.opsManagerDate ? new Date(warning.opsManagerDate).toLocaleDateString('en-GB') : ''}`}>
                      <Chip label={`${getStatusLabel(warning.status)} - ${warning.opsManagerName || ''}`} color="warning" size="small" />
                    </Tooltip>
                  ) : warning.status === 'Approved' ? (
                    <Tooltip title={`${isRTL ? 'اعتمد بواسطة' : 'Approved by'}: ${warning.hrManagerName || ''} - ${warning.hrApprovalDate ? new Date(warning.hrApprovalDate).toLocaleDateString('en-GB') : ''}`}>
                      <Chip label={`${getStatusLabel(warning.status)} - ${warning.hrManagerName || ''}`} color="success" size="small" />
                    </Tooltip>
                  ) : (
                    <Chip label={getStatusLabel(warning.status)} color={getStatusColor(warning.status)} size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  {(warning.status === 'Active' || warning.status === 'Acknowledged') && (
                    <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {/* Ops Manager acknowledge — only if no APPROVE permission */}
                      {warning.status === 'Active' && !warning.opsManagerAcknowledged && permissions?.includes('WARNING_ACKNOWLEDGE') && !permissions?.includes('WARNING_APPROVE') && (
                        <Tooltip title={isRTL ? 'موافقة أولية' : 'Acknowledge'}>
                          <IconButton size="small" color="warning" onClick={() => handleAcknowledge(warning.warningId)}><CheckIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                      {/* Final approve — only HR/SuperAdmin with WARNING_APPROVE */}
                      {(warning.status === 'Active' || warning.status === 'Acknowledged') && permissions?.includes('WARNING_APPROVE') && (
                        <>
                          <Tooltip title={isRTL ? 'اعتماد نهائي' : 'Approve'}>
                            <IconButton size="small" color="success" onClick={() => handleApprove(warning.warningId, 'Approved')}><GavelIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title={isRTL ? 'رفض' : 'Reject'}>
                            <IconButton size="small" color="error" onClick={() => handleApprove(warning.warningId, 'Rejected')}><CloseIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </>
                      )}
                      {/* Edit/Delete — Active or Acknowledged, by branch member */}
                      {(warning.status === 'Active' || warning.status === 'Acknowledged') && canEditWarning(warning.branchId) && !warning.opsManagerAcknowledged && (
                        <>
                          <IconButton size="small" onClick={() => { setSelectedWarning(warning); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(warning.warningId)}><DeleteIcon fontSize="small" /></IconButton>
                        </>
                      )}
                    </Box>
                  )}
                  <AttachmentSection documentType="Warning" documentId={warning.warningId} isRTL={isRTL} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <WarningDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setSelectedWarning(null); }}
        onSave={loadData}
        warning={selectedWarning}
      />
    </Box>
  );
};

export default WarningsPage;